"use client";

import { useMemo, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import TIcon from "@/public/icon/Ticon.svg";
import MaskedImage from "../MaskedImage";
import CircleArrow from "@/components/iconComponents/CircleArrow";
import { getBlogPostHref, getBlogPostPathSegment } from "@/lib/utils/blog";
import { getImageUrl } from "@/lib/utils/image";

const getPostValue = (post, index) =>
  getBlogPostPathSegment(post) ||
  post?.id?.toString() ||
  `item-${index + 1}`;

export default function BlogSectionClient({ posts = [] }) {
  const values = useMemo(
    () => posts.map((post, index) => getPostValue(post, index)),
    [posts]
  );
  const [activeValue, setActiveValue] = useState(values[0] || "item-1");

  const activePost =
    posts[values.indexOf(activeValue)] || posts[0] || null;
  const activeHref = getBlogPostHref(activePost);
  const activeImageSrc = getImageUrl(
    activePost?.image || activePost?.image_url
  );

  return (
    <div className="flex lg:flex-row flex-wrap md:flex-nowrap justify-center items-start lg:gap-14">
      <div className="w-full lg:w-[552px]">
        <Accordion
          type="single"
          collapsible
          className="w-full"
          value={activeValue}
          onValueChange={(value) => value && setActiveValue(value)}
        >
          {posts.map((post, index) => {
            const title = post.title || "";
            const rawContent =
              post.content || post.desc || post.description || "";
            const content = rawContent.replace(/<[^>]*>/g, "");
            const link = getBlogPostHref(post);

            return (
              <AccordionItem
                key={post.id || values[index]}
                value={values[index]}
                className="border-none mb-4"
              >
                <div className="bg-black-light/45 rounded-xl overflow-hidden transition-all">
                  <AccordionTrigger className="group text-right text-bold text-lg flex justify-start gap-4 hover:no-underline px-4 py-3 transition-all rounded-none border-none">
                    <Image
                      src={TIcon}
                      alt=""
                      aria-hidden="true"
                      width={48}
                      height={48}
                      className="w-12 h-12 shrink-0"
                    />
                    <span className="flex-1">{title}</span>
                  </AccordionTrigger>

                  <AccordionContent className="text-mute ps-4 pe-4 pb-4 pt-0">
                    <div className="ps-16 pt-4">
                      <p className="leading-relaxed line-clamp-3 mb-6">
                        {content}
                      </p>
                      {link ? (
                        <CircleArrow
                          href={link}
                          className="rotate-180"
                          aria-label={`خواندن مقاله ${title}`}
                        />
                      ) : null}
                    </div>
                  </AccordionContent>
                </div>
              </AccordionItem>
            );
          })}
        </Accordion>
      </div>

      {activeImageSrc ? (
        activeHref ? (
          <Link
            href={activeHref}
            className="block w-full lg:w-[472px]"
            aria-label={activePost?.title}
          >
            <MaskedImage
              key={activeImageSrc}
              src={activeImageSrc}
              size="sm"
              alt={activePost?.title || "تصویر بلاگ اسمارت"}
              className="w-full h-full lg:w-[472px] lg:h-[472px]"
            />
          </Link>
        ) : (
          <MaskedImage
            key={activeImageSrc}
            src={activeImageSrc}
            size="sm"
            alt={activePost?.title || "تصویر بلاگ اسمارت"}
            className="w-full h-full lg:w-[472px] lg:h-[472px]"
          />
        )
      ) : null}
    </div>
  );
}
